/**
 * E-Prescription Organization Setup Modal
 *
 * Modal for setting up the organization's DoseSpot clinic connection.
 * Collects clinic information required for DoseSpot integration.
 */

import { useEffect, useState } from 'react';
import { useOrganizationAccess } from '@features/organizations/store';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { matchIsValidTel, MuiTelInput } from 'mui-tel-input';
import { useSnackbar } from 'notistack';

import { US_STATES } from '@shared/constants/states';

import { useSetupClinicMutation } from '../store';

/** Extract 10-digit phone number from formatted input */
const extractDigits = (phone: string): string => {
  const digits = phone.replace(/\D/g, '');
  // Remove country code (1) if present
  return digits.startsWith('1') && digits.length === 11 ? digits.slice(1) : digits;
};

interface EPrescriptionOrgSetupModalProps {
  open: boolean;
  onClose: () => void;
}

interface FormState {
  clinicName: string;
  phone: string;
  addressLine1: string;
  city: string;
  state: string;
  zipCode: string;
}

const initialFormState: FormState = {
  clinicName: '',
  phone: '',
  addressLine1: '',
  city: '',
  state: '',
  zipCode: '',
};

export function EPrescriptionOrgSetupModal({ open, onClose }: EPrescriptionOrgSetupModalProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { organizationData } = useOrganizationAccess();
  const [setupClinic, { isLoading: isSubmitting }] = useSetupClinicMutation();

  const [form, setForm] = useState<FormState>(initialFormState);
  const [error, setError] = useState<string | null>(null);
  const [errorField, setErrorField] = useState<keyof FormState | null>(null);

  // Pre-fill form with organization data when modal opens
  useEffect(() => {
    if (open) {
      setForm({
        clinicName: organizationData?.name || '',
        phone: '',
        addressLine1: '',
        city: '',
        state: '',
        zipCode: '',
      });
      setError(null); setErrorField(null);
    }
  }, [open, organizationData?.name]);

  const handleChange = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    if (error) { setError(null); setErrorField(null); }
  };

  const handlePhoneChange = (value: string) => {
    setForm(prev => ({ ...prev, phone: value }));
    if (error) { setError(null); setErrorField(null); }
  };

  const validateForm = (): boolean => {
    if (!form.clinicName.trim()) {
      setError('Clinic name is required');
      return false;
    }
    if (!matchIsValidTel(form.phone) || extractDigits(form.phone).length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return false;
    }
    if (!form.addressLine1.trim()) {
      setError('Address is required');
      return false;
    }
    if (!form.city.trim()) {
      setError('City is required');
      return false;
    }
    if (!form.state.trim()) {
      setError('State is required');
      setErrorField('state');
      return false;
    }
    if (!(form.state.trim().toUpperCase() in US_STATES)) {
      setError('Please use a two-letter state abbreviation (e.g., MA, CA, NY)');
      setErrorField('state');
      return false;
    }
    if (!form.zipCode.trim()) {
      setError('ZIP code is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      await setupClinic({
        setupClinicRequest: {
          clinic: {
            clinic_name: form.clinicName.trim(),
            phone: extractDigits(form.phone),
            address_line1: form.addressLine1.trim(),
            city: form.city.trim(),
            state: form.state.trim(),
            zip_code: form.zipCode.trim(),
          },
        },
      }).unwrap();

      enqueueSnackbar('E-Prescription clinic connected successfully', { variant: 'success' });
      onClose();
    } catch (err: unknown) {
      let errorMessage = 'Failed to set up clinic. Please try again.';

      const parseValidationErrors = (errors: unknown[]): string => {
        return errors
          .map((e: unknown) => {
            const error = e as { msg?: string; loc?: string[] };
            return error.msg?.replace('Value error, ', '') || 'Validation error';
          })
          .join('. ');
      };

      // Handle error - RTK Query wraps as { data: { data: [...], message } }
      if (err && typeof err === 'object' && 'data' in err) {
        const outerData = (err as { data?: unknown }).data;

        if (outerData && typeof outerData === 'object') {
          const innerData = outerData as { data?: unknown; message?: string; detail?: unknown };

          if (Array.isArray(innerData.data)) {
            errorMessage = parseValidationErrors(innerData.data) || errorMessage;
          } else if (Array.isArray(innerData.detail)) {
            errorMessage = parseValidationErrors(innerData.detail) || errorMessage;
          } else if (innerData.message && innerData.message !== 'Unknown error occurred') {
            errorMessage = innerData.message;
          }
        } else if (Array.isArray(outerData)) {
          errorMessage = parseValidationErrors(outerData) || errorMessage;
        }
      }

      setError(errorMessage);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Connect to E-Prescriptions</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, mt: 1 }}>
          Set up your organization&apos;s DoseSpot clinic to enable e-prescribing for your clinicians.
          This information will be used to register your practice with DoseSpot.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Clinic Name"
              value={form.clinicName}
              onChange={handleChange('clinicName')}
              disabled={isSubmitting}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <MuiTelInput
              fullWidth
              label="Phone Number"
              value={form.phone}
              onChange={handlePhoneChange}
              disabled={isSubmitting}
              defaultCountry="US"
              onlyCountries={['US']}
              forceCallingCode
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Street Address"
              value={form.addressLine1}
              onChange={handleChange('addressLine1')}
              disabled={isSubmitting}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="City"
              value={form.city}
              onChange={handleChange('city')}
              disabled={isSubmitting}
              required
            />
          </Grid>

          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              label="State"
              value={form.state}
              onChange={handleChange('state')}
              disabled={isSubmitting}
              required
              placeholder="MA"
              error={errorField === 'state'}
              helperText={errorField === 'state' ? error : 'Two-letter abbreviation'}
            />
          </Grid>

          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              label="ZIP Code"
              value={form.zipCode}
              onChange={handleChange('zipCode')}
              disabled={isSubmitting}
              required
              placeholder="02101"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
        >
          {isSubmitting ? 'Connecting...' : 'Connect Clinic'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
